// What the mind map canvas draws, worked out apart from React so it's tested in Node: a charcoal or pencil mark as a
// filled ribbon (its points offset by half the width either side), what a linked note shows (a document's title, or a
// character's or place's name and colour), a note's words for a screen reader, and where things go on screen.
import { linkTitle } from './mapProject.ts';
import {
  MapInk, MapLayout, mapNeighbours, type MapContent, type MapFrame, type MapLink, type MapNote, type MapPoint, type MapStroke, type MapStrokePoint, type Project, type SketchMark,
  type SketchPoint,
} from '@writing-suite/core';

const n = (v: number) => (Math.round(v * 100) / 100).toString();

/** A stroke as a filled outline: along the points offset by +width/2 on the local normal, then back by −width/2. */
export function ribbonPath(points: readonly SketchPoint[]): string {
  if (points.length < 2) return '';
  const left: string[] = [], right: string[] = [];
  for (let i = 0; i < points.length; i++) {
    const a = points[Math.max(0, i - 1)], b = points[Math.min(points.length - 1, i + 1)];
    let dx = b.x - a.x, dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (length > 0) {
      dx /= length;
      dy /= length;
    }
    const h = Math.max(points[i].width, 0.15) / 2;
    const p = points[i];
    left.push(`${n(p.x - dy * h)} ${n(p.y + dx * h)}`);
    right.push(`${n(p.x + dy * h)} ${n(p.y - dx * h)}`);
  }
  return `M${left.join(' L')} L${right.reverse().join(' L')} Z`;
}

/** A filled mark (a region's wash): its points as one closed shape. */
export function fillPath(points: readonly SketchPoint[]): string {
  if (points.length < 3) return '';
  return `M${points.map((p) => `${n(p.x)} ${n(p.y)}`).join(' L')} Z`;
}

/** The marks' path data, a stroke as a ribbon and a fill as its shape. */
export const markPath = (mark: SketchMark) => (mark.kind === 'fill' ? fillPath(mark.points) : ribbonPath(mark.points));

/** A mark's points for a polyline (a dotted line's dashes, a line's hit area). */
export const polylinePoints = (points: readonly { x: number; y: number }[]) => points.map((p) => `${n(p.x)},${n(p.y)}`).join(' ');

/** The middle of a line as drawn: where its label sits. */
export function midPoint(points: readonly { x: number; y: number }[]): { x: number; y: number } | null {
  if (!points.length) return null;
  return points[Math.floor(points.length / 2)];
}

// MARK: Linked notes

/** What a note linked to the project shows. */
export type LinkShown =
  | { kind: 'document'; title: string; synopsis: string; trashed: boolean; missing: false }
  | { kind: 'entity'; title: string; color: string | null; missing: false }
  | { kind: 'document' | 'entity'; title: string; missing: true };

/** A document's current title (and synopsis), or an entity's name (the note's own words when they name them) and colour;
 *  a link to something gone keeps the note's own words. */
export function linkShown(project: Pick<Project, 'fragment' | 'entities'>, link: MapLink, fallback: string): LinkShown {
  if (link.kind === 'fragment') {
    const f = project.fragment(link.id);
    if (!f) return { kind: 'document', title: fallback.trim() || 'A deleted document', missing: true };
    return { kind: 'document', title: f.title.trim() || 'Untitled', synopsis: f.synopsis.trim(), trashed: f.trashed, missing: false };
  }
  const e = project.entities().find((x) => x.id === link.id);
  if (!e) return { kind: 'entity', title: fallback.trim() || 'Someone no longer in the cast', missing: true };
  return { kind: 'entity', title: linkTitle(project, link, fallback) ?? e.name, color: e.color ?? null, missing: false };
}

/** A note's words as shown (a link's title in place of its own words). */
export function noteWords(project: Pick<Project, 'fragment' | 'entities'>, note: MapNote): string {
  return note.link ? linkShown(project, note.link, note.text).title : note.text;
}

/** One line of a note's words: lines joined by spaces. */
const LINE_BREAKS = new RegExp('\\r\\n|[\\n\\r' + String.fromCharCode(0x2028, 0x2029) + ']');
const oneLine = (text: string) => text.split(LINE_BREAKS).map((s) => s.trim()).filter(Boolean).join(' ');

/** A note for a screen reader: "The door, joined to Utterson, Story of the Door". */
export function noteLabel(content: MapContent, id: string, words: (note: MapNote) => string): string {
  const byID = new Map(content.notes.map((x) => [x.id, x]));
  const note = byID.get(id);
  if (!note) return '';
  const own = oneLine(words(note)) || 'Empty note';
  const joined = [...new Set(mapNeighbours(content, id))].flatMap((other) => {
    const o = byID.get(other);
    return o ? [oneLine(words(o)) || 'an empty note'] : [];
  });
  const centre = content.centre === id ? ', the centre' : '';
  return joined.length ? `${own}${centre}, joined to ${joined.join(', ')}` : `${own}${centre}`;
}

// MARK: Screen and paper

export interface MapView { x: number; y: number; k: number }

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 4;
export const clampZoom = (k: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, k));

/** A point on screen (relative to the canvas's corner) on the paper. */
export const toPaper = (view: MapView, x: number, y: number) => ({ x: (x - view.x) / view.k, y: (y - view.y) / view.k });

/** Zooms to `k` keeping the paper under (x, y) where it is. */
export function zoomAbout(view: MapView, k: number, x: number, y: number): MapView {
  const to = clampZoom(k);
  const p = toPaper(view, x, y);
  return { k: to, x: x - p.x * to, y: y - p.y * to };
}

/** The view that shows `bounds` in a `width` × `height` canvas with room round it, no closer than `most`. */
export function fitView(bounds: MapFrame | null, width: number, height: number, most = 1.5): MapView {
  if (!bounds || width <= 0 || height <= 0) return { x: width / 2, y: height / 2, k: 1 };
  const pad = Math.min(80, Math.max(24, Math.min(width, height) * 0.08));
  const k = clampZoom(Math.min(most, (width - pad * 2) / Math.max(bounds.width, 1), (height - pad * 2) / Math.max(bounds.height, 1)));
  return { k, x: width / 2 - (bounds.x + bounds.width / 2) * k, y: height / 2 - (bounds.y + bounds.height / 2) * k };
}

/** The notes whose frames a marquee (any corner to any corner) touches. */
export function inMarquee(frames: ReadonlyMap<string, MapFrame>, a: { x: number; y: number }, b: { x: number; y: number }): string[] {
  const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x), y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
  return [...frames].filter(([, f]) => f.x < x1 && f.x + f.width > x0 && f.y < y1 && f.y + f.height > y0).map(([id]) => id);
}

/** The frame round several frames. */
export function unionFrame(frames: MapFrame[]): MapFrame | null {
  if (!frames.length) return null;
  const x0 = Math.min(...frames.map((f) => f.x)), y0 = Math.min(...frames.map((f) => f.y));
  const x1 = Math.max(...frames.map((f) => f.x + f.width)), y1 = Math.max(...frames.map((f) => f.y + f.height));
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/** Where a line leaves a note being joined from, towards a point (the knob's provisional line). */
export const edgeToward = (frame: MapFrame, note: MapNote, x: number, y: number) => MapLayout.edgePoint(frame, note.outline, { x, y });

// MARK: Ink

/** How wide the pen draws at a pressure (as the Apple canvas draws it): from 0.4 of the stroke's width to all of it. */
export const penWidth = (width: number, pressure: number) => width * (0.4 + 0.6 * pressure);

/** A stroke's points as drawn: moved by (dx, dy) (a drag), each as wide as the pen pressed there. */
export function strokeMarkPoints(points: readonly MapStrokePoint[], width: number, dx = 0, dy = 0): SketchPoint[] {
  return points.map((p) => ({ x: p.x + dx, y: p.y + dy, width: penWidth(width, p.pressure) }));
}

/** A pen's pressure at a pointer: a pen's own (0.5 when it reports none), else 1 (a mouse or a finger presses evenly). */
export function pointerPressure(e: { pointerType: string; pressure: number }): number {
  if (e.pointerType !== 'pen') return 1;
  return e.pressure > 0 ? Math.min(1, e.pressure) : 0.5;
}

/** To a tenth of a point, halves away from zero (Swift's `rounded()`), as stored. */
const tenth = (v: number) => (Math.sign(v) * Math.round(Math.abs(v) * 10)) / 10 || 0;

/** The chosen strokes' whole offsets after they're dragged by (dx, dy), to a tenth of a point (`moveMapStrokes`). */
export function strokeOffsets(strokes: readonly MapStroke[], ids: Iterable<string>, dx: number, dy: number): Map<string, MapPoint> {
  const chosen = new Set(ids);
  return new Map(strokes.filter((s) => chosen.has(s.id)).map((s) => [s.id, { x: tenth(s.offset.x + dx), y: tenth(s.offset.y + dy) }] as const));
}

/** The strokes whose frames (at least a point across) a marquee touches. */
export function strokesInMarquee(strokes: readonly MapStroke[], a: { x: number; y: number }, b: { x: number; y: number }): string[] {
  const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x), y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
  return strokes.filter((s) => {
    const f = MapInk.bounds([s]);
    return !!f && f.x < x1 && f.x + Math.max(f.width, 1) > x0 && f.y < y1 && f.y + Math.max(f.height, 1) > y0;
  }).map((s) => s.id);
}
